/**
 * Live attendance roster for a session (event occurrence). Shows who is checked
 * in / out, minors PII (allergies/medical notes) when the caller is permitted,
 * and lets operators void an erroneous record. Checkout itself happens at the
 * kiosk (it needs the pickup code), so this view links back to the station.
 */
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Spinner } from '@fc/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, HeartPulse, ScanLine, ShieldAlert, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import { listEvents, listOccurrences, type EventOccurrenceDto } from '../../lib/events-api'
import {
  getRoster,
  voidCheckIn,
  type CheckInDto,
  type RosterEntryDto,
} from '../../lib/checkins-api'

const statusVariant: Record<string, 'success' | 'neutral' | 'danger' | 'gold'> = {
  checked_in: 'success',
  checked_out: 'neutral',
  no_show: 'gold',
  voided: 'danger',
}

function formatTime(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

function RosterRow({ entry, occurrenceId }: { entry: RosterEntryDto; occurrenceId: string }) {
  const c: CheckInDto = entry.checkIn
  const queryClient = useQueryClient()
  const remove = useMutation({
    mutationFn: () => voidCheckIn(c.id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['roster', occurrenceId] }),
  })
  const hasMinorPii = c.allergies != null || c.medicalNotes != null

  return (
    <tr className="border-b border-border-soft transition-colors last:border-0 hover:bg-card-hover">
      <td className="px-5 py-3">
        <div className="flex items-center gap-2">
          <span className="font-medium">{c.personName ?? 'Unknown'}</span>
          {c.isMinor && (
            <Badge variant="gold" className="text-[10px]">
              Minor
            </Badge>
          )}
          {c.pickupOverride && (
            <span title="Released via audited pickup override">
              <ShieldAlert className="size-3.5 text-warning" />
            </span>
          )}
        </div>
        {hasMinorPii && (
          <div className="mt-1 flex items-center gap-1.5 text-xs text-warning">
            <HeartPulse className="size-3.5" />
            <span>
              {c.allergies ? `Allergies: ${c.allergies}` : ''}
              {c.allergies && c.medicalNotes ? ' · ' : ''}
              {c.medicalNotes ?? ''}
            </span>
          </div>
        )}
      </td>
      <td className="px-5 py-3 text-muted">{formatTime(c.checkedInAt)}</td>
      <td className="px-5 py-3 text-muted">{formatTime(c.checkedOutAt)}</td>
      <td className="px-5 py-3">
        <Badge variant={statusVariant[c.status] ?? 'neutral'} className="capitalize">
          {c.status.replace('_', ' ')}
        </Badge>
      </td>
      <td className="px-5 py-3 text-right">
        {c.status === 'checked_in' && (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Void check-in"
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
          >
            <Trash2 />
          </Button>
        )}
      </td>
    </tr>
  )
}

export function CheckinRoster() {
  const [eventId, setEventId] = useState('')
  const [occurrenceId, setOccurrenceId] = useState('')
  const [activeOnly, setActiveOnly] = useState(true)

  const eventsQuery = useQuery({ queryKey: ['checkin-events'], queryFn: listEvents })
  const checkinEvents = (eventsQuery.data ?? []).filter((e) => e.checkinEnabled)

  const occQuery = useQuery({
    queryKey: ['checkin-occurrences', eventId],
    queryFn: () => listOccurrences(eventId),
    enabled: !!eventId,
  })

  const rosterQuery = useQuery({
    queryKey: ['roster', occurrenceId, activeOnly],
    queryFn: () => getRoster(occurrenceId, activeOnly),
    enabled: !!occurrenceId,
  })

  const entries = rosterQuery.data ?? []
  const presentCount = entries.filter((e) => e.checkIn.status === 'checked_in').length

  return (
    <div className="space-y-6">
      <Link
        to="/app/checkins"
        className="inline-flex items-center gap-1.5 text-sm text-muted transition-colors hover:text-fg"
      >
        <ArrowLeft className="size-4" /> Check-in
      </Link>

      <PageHeader
        title="Roster"
        description="Who is checked in for a session."
        actions={
          <Button asChild variant="secondary">
            <Link to="/app/checkins/kiosk">
              <ScanLine /> Station
            </Link>
          </Button>
        }
      />

      <Card>
        <CardContent className="grid gap-3 p-5 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted">Event</label>
            <select
              value={eventId}
              onChange={(e) => {
                setEventId(e.target.value)
                setOccurrenceId('')
              }}
              className="h-10 w-full rounded-md border border-border bg-bg-alt px-3 text-sm text-fg focus-visible:border-secondary focus-visible:outline-none"
            >
              <option value="">Select an event…</option>
              {checkinEvents.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted">Session</label>
            <select
              value={occurrenceId}
              onChange={(e) => setOccurrenceId(e.target.value)}
              disabled={!eventId || occQuery.isLoading}
              className="h-10 w-full rounded-md border border-border bg-bg-alt px-3 text-sm text-fg focus-visible:border-secondary focus-visible:outline-none disabled:opacity-50"
            >
              <option value="">Select a session…</option>
              {(occQuery.data ?? []).map((o: EventOccurrenceDto) => (
                <option key={o.id} value={o.id}>
                  {new Date(o.startsAt).toLocaleString(undefined, {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {occurrenceId && (
        <Card className="overflow-hidden p-0">
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle>Attendance</CardTitle>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs text-muted">
                <input
                  type="checkbox"
                  checked={activeOnly}
                  onChange={(e) => setActiveOnly(e.target.checked)}
                  className="size-3.5 rounded border-border"
                />
                Currently present only
              </label>
              <Badge variant="success">{presentCount} present</Badge>
            </div>
          </CardHeader>
          {rosterQuery.isLoading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-muted">
              <Spinner /> Loading roster…
            </div>
          ) : entries.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted">
              No check-ins for this session yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-dim">
                    <th className="px-5 py-3 font-semibold">Name</th>
                    <th className="px-5 py-3 font-semibold">In</th>
                    <th className="px-5 py-3 font-semibold">Out</th>
                    <th className="px-5 py-3 font-semibold">Status</th>
                    <th className="px-5 py-3 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <RosterRow key={e.checkIn.id} entry={e} occurrenceId={occurrenceId} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </div>
  )
}
